import { Component } from 'react'
import type { ReactNode } from 'react'

import { useFlags } from 'launchdarkly-react-client-sdk'

import {
  AuthContextProvider,
  Braze,
  defaultContextValue,
} from '@b-stock/fe-core/bstock-next/components'
import { ddLogger } from '@b-stock/fe-core/bstock-react'

import appConfig from '@config/config'
import { usePerson } from '@queries/person'

type BrazeErrorBoundaryProps = { children: ReactNode; fallback: ReactNode }

class BrazeErrorBoundary extends Component<
  BrazeErrorBoundaryProps,
  { hasError: boolean }
> {
  static getDerivedStateFromError() {
    return { hasError: true }
  }

  constructor(props: BrazeErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    ddLogger.error(
      'BrazeContainer render error — Braze degraded gracefully',
      { info },
      error instanceof Error
        ? error
        : new Error('BrazeContainer error', { cause: error })
    )
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
    }
    return this.props.children
  }
}

const BrazeContainer = ({ children }: { children: ReactNode }) => {
  const { 'enable-braze-marketing-popups': enableBraze = false } = useFlags<{
    'enable-braze-marketing-popups'?: boolean
  }>()

  const { data: person } = usePerson()

  return (
    <BrazeErrorBoundary fallback={children}>
      <AuthContextProvider
        value={{ ...defaultContextValue, isDefaultContext: false }}
      >
        <Braze
          enabled={enableBraze}
          apiKey={appConfig.braze.apiKey}
          apiEndpoint={appConfig.braze.apiEndpoint}
          bypassConsent={Boolean(appConfig.segment.bypassConsent)}
          allowUserSuppliedJavascript={
            appConfig.braze.allowUserSuppliedJavascript
          }
          person={person ?? null}
        >
          {children}
        </Braze>
      </AuthContextProvider>
    </BrazeErrorBoundary>
  )
}

export default BrazeContainer
